import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';

import connect from 'components/lib/connect';
import { GRAYS } from 'components/common/colors';
import InsetContainer from 'components/common/containers/inset_container';
import LoadingSpinner from 'components/lib/loading_spinner';
import NavigateTo from 'actions/current_location/navigate_to';
import TeamEditor from 'models/location/team_editor';
import TeamSearch from 'components/admin/users/team_search';
import StackContainer from 'components/common/containers/stack_container';

export const NoTeamsMessage = styled.div`
  color: ${p => p.theme.colors.gray900};
`;

export const TeamContentWrapper = styled(StackContainer)`
  flex-direction: row;
  &:hover {
    color: ${p => p.theme.colors.green500};
    cursor: pointer;
  }
`;

const TeamContentName = styled.div`
  flex-basis: 85%;
  word-break: break-all;
`;

const TeamContentAgentCount = styled.div`
  flex-basis: 15%;
  text-align: right;
`;

const RefineSearchMessage = styled.div`
  color: ${p => p.theme.colors.gray400};
  padding-top: ${p => p.theme.spacing.inlineMedium};
`;

export const MAX_TEAMS_DISPLAY_COUNT = 25;

export function TeamsList({ isLoading, onTeamClick, teams, textFilter, totalSearchLength, totalLength }) {
  if (isLoading) {
    return <LoadingSpinner color={GRAYS.xlight} position="relative" />;
  }

  if (teams.length === 0) {
    if (textFilter) {
      return <NoTeamsMessage>No teams found</NoTeamsMessage>;
    }
    return <NoTeamsMessage>No teams have been created</NoTeamsMessage>;
  }

  const totalTeams = textFilter ? totalSearchLength : totalLength;

  return (
    <InsetContainer inset="none">
      {_.map(teams, renderTeam)}
      {totalTeams > MAX_TEAMS_DISPLAY_COUNT ? (
        <RefineSearchMessage data-aid="refineSearchMessage">
          Showing {teams.length} of {totalTeams} teams. Refine your search for better results
        </RefineSearchMessage>
      ) : null}
    </InsetContainer>
  );

  function renderTeam(team) {
    return (
      <TeamContentWrapper inset="none" key={team.id} onClick={handleTeamClick.bind(null, team.id)}>
        <TeamContentName>{team.name}</TeamContentName>
        <TeamContentAgentCount>{team.agentsCount}</TeamContentAgentCount>
      </TeamContentWrapper>
    );
  }

  function handleTeamClick(teamId) {
    onTeamClick && onTeamClick(teamId);
  }
}

TeamsList.propTypes = {
  isLoading: PropTypes.bool,
  onTeamClick: PropTypes.func,
  teams: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      agentsCount: PropTypes.number.isRequired,
    })
  ),
  textFilter: PropTypes.string.isRequired,
  totalLength: PropTypes.number,
  totalSearchLength: PropTypes.number,
};

export class TeamsListSearcher extends React.Component {
  constructor(props) {
    super(props);
    this.state = {};
  }

  static getDerivedStateFromProps({ teamSearch, teams, textFilter }, state) {
    if (!textFilter) {
      if (teamSearch !== state.teamSearch || textFilter !== state.textFilter) {
        let teamsList = _.sortBy(teamSearch.list, team => team.name.toLowerCase());
        return {
          teamSearch,
          teams: teamsList.slice(0, MAX_TEAMS_DISPLAY_COUNT),
          textFilter,
          totalLength: teamSearch.list.length,
          totalSearchLength: 0,
        };
      }
    } else if (teamSearch !== state.teamSearch || textFilter !== state.textFilter) {
      teams = teamSearch.search(textFilter);
      return {
        teamSearch,
        teams: teams.slice(0, MAX_TEAMS_DISPLAY_COUNT),
        textFilter,
        totalLength: teamSearch.list.length,
        totalSearchLength: teams.length,
      };
    }
    return null;
  }

  render() {
    return (
      <TeamsList
        isLoading={this.props.isLoading}
        onTeamClick={this.props.onTeamClick}
        teams={this.state.teams}
        textFilter={this.props.textFilter}
        totalSearchLength={this.state.totalSearchLength}
        totalLength={this.state.totalLength}
      />
    );
  }
}

TeamsListSearcher.propTypes = {
  ...TeamsList.propTypes,
  teamSearch: PropTypes.object,
};

function TeamsListWithSearch(props) {
  return <TeamSearch>{({ teamSearch }) => <TeamsListSearcher {...props} teamSearch={teamSearch} />}</TeamSearch>;
}

export { TeamsListWithSearch };

const TeamsListContainer = connect(mapStateToProps, mapExecuteToProps)(TeamsListWithSearch);

TeamsListContainer.propTypes = {
  textFilter: PropTypes.string,
};

function mapStateToProps({ getProvider }) {
  return {
    isLoading: getProvider('teams').isLoading(),
  };
}

function mapExecuteToProps(executeAction) {
  return {
    onTeamClick: teamId => executeAction(NavigateTo, TeamEditor.create({ teamId })),
  };
}

export default TeamsListContainer;



// WEBPACK FOOTER //
// ./src/scripts/presentation/components/admin/users/teams_list.jsx